import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "Core/queryClient";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";

// Fuente única de las llamadas de Clip Request (video) con TanStack Query.
// Los componentes consumen estos hooks directamente. La invalidación de caché vive en
// cada función de mutación (no en los componentes): al resolver con éxito, create/update/
// delete/restore invalidan solos las keys relacionadas, usen el hook o la función directa.

export const clipRequestKeys = {
  all: ["videoClips"],
  list: ["videoClips", "requested"],
  detail: (id) => ["videoClips", "detail", id],
};

export const searchClipRequestsQuery = `
  query searchClipRequests ($openSearch: String, $entities: [EntityInput]!, $hasNotes: Boolean, $hasStarred: Boolean, $isDeleted: Boolean, $dateRange: AWSDateTimeRange!, $limit: Int, $offset: Int, $status: String) {
    searchClipRequests (openSearch: $openSearch, entities: $entities, hasNotes: $hasNotes, hasStarred: $hasStarred, isDeleted: $isDeleted, dateRange: $dateRange, limit: $limit, offset: $offset, status: $status) {
      items {
        id accountId createdOn deviceId esn label status time tripID clipID rotate url startDate endDate
        notes starred errorMsg deviceModelId deviceModelName driverName driverExternalId thumbnailUrl
        transactionId details isDeleted isPrivate createdBy
        unit { timeZone { id name } tags { id label color } }
        createdByUser { authId username }
      }
      total
    }
  }`;

const GET_CLIP_REQUEST = `
  query ($id: String!) {
    Clip: getClipRequest (id: $id) {
      id accountId createdOn deviceId esn label status time tripID clipID rotate url startDate endDate
      notes starred errorMsg deviceModelId deviceModelName isDeleted thumbnailUrl details
      unit { driverName driverExternalId label }
    }
  }`;

const CREATE_CLIP_REQUEST = `
  mutation createClipRequest (
    $dateRange: AWSDateTimeRange!, $deviceId: String!, $thumbnailUrl: String, $transactionId: String,
    $tripID: String, $mp4Url: String, $rotate: String, $clipID: String, $latitude: Float, $longitude: Float, $isPrivate: Boolean
  ) {
    createClipRequest (
      deviceId: $deviceId, dateRange: $dateRange, thumbnailUrl: $thumbnailUrl, transactionId: $transactionId,
      tripID: $tripID, mp4Url: $mp4Url, rotate: $rotate, clipID: $clipID, latitude: $latitude, longitude: $longitude, isPrivate: $isPrivate
    ) {
      id accountId createdOn deviceId label status time tripID clipID rotate url startDate endDate
      notes starred errorMsg deviceModelId deviceModelName unitId unit { id label }
    }
  }`;

const CREATE_SMARTWITNESS_CLIP_REQUEST = `
  mutation createSmartWitnessClipRequest ($date: AWSDateTime!, $deviceId: String, $durationBefore: Int, $durationAfter: Int, $esn: String!, $mediaType: String!) {
    createSmartWitnessClipRequest (date: $date, deviceId: $deviceId, durationBefore: $durationBefore, durationAfter: $durationAfter, esn: $esn, mediaType: $mediaType) {
      error errorInfo { Message } data { id }
    }
  }`;

const UPDATE_CLIP_REQUEST = `
  mutation updateClipRequest ($id: String!, $notes: String, $starred: Boolean) {
    ClipRequest: updateClipRequest (id: $id, notes: $notes, starred: $starred) { id notes starred }
  }`;

const DELETE_CLIP_REQUESTS = `
  mutation DeleteClipsRequest($ids: [String]!) { data: deleteClipsRequest(ids: $ids) }`;

const invalidate = (...queryKeys) =>
  Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));

// Ejecuta `mutation` y, solo si resuelve, invalida en segundo plano: el refetch de la lista no
// bloquea la mutación (el modal cierra de inmediato) y la lista se refresca sin estado de loading.
const withInvalidation = (mutation, getKeys) => async (variables) => {
  const result = await mutation(variables);
  void invalidate(...getKeys(variables, result));
  return result;
};

// Para actualizaciones por push notification: refresca la lista de clips sin reiniciar un
// refetch que ya esté en curso (varias notificaciones seguidas no se cancelan entre sí).
export const refreshClipRequests = () =>
  queryClient.invalidateQueries({ queryKey: clipRequestKeys.all }, { cancelRefetch: false });

const buildBatchMutation = (alias, field, ids) =>
  `mutation { ${ids.map((id, i) => `${alias}${i}: ${field}(id: ${JSON.stringify(String(id))})`).join(" ")} }`;

export const buildSearchClipsVariables = ({
  limit, dateRange, offset, entities, hasStarred, hasNotes, isDeleted, status, openSearch,
}) => ({
  dateRange,
  offset,
  limit,
  entities: !entities || !entities?.length || entities.find((e) => e.id == -1) ? [{ name: "All", id: -1 }] : entities,
  isDeleted: isDeleted || false,
  hasStarred: hasStarred || false,
  hasNotes: hasNotes || false,
  status: status || "all",
  openSearch,
});

// ---- Fetchers (funciones puras, reutilizables fuera de React) ----
export const searchClipRequests = (variables) =>
  clientQuery(searchClipRequestsQuery, variables, endpoints.GRAPHQL_GENERAL).then((data) => data.searchClipRequests);

export const getClipRequest = (id) =>
  clientQuery(GET_CLIP_REQUEST, { id }, endpoints.GRAPHQL_GENERAL).then((data) => data.Clip);

// Códigos de error del backend -> key de mensaje i18n (se expone como `error.messageKey`).
const CREATE_ERROR_MESSAGE_KEYS = {
  1011: "infoBubble_cameraNotFound",
  1001: "infoBubble_requestRangeTimeError",
};

const toCreateClipError = (exc) => {
  const codes = (exc?.errorData?.errors || []).map((e) => e?.errorInfo?.code).filter(Boolean);
  const error = exc instanceof Error ? exc : new Error("Clip has not been created");
  error.errorCode = codes[codes.length - 1] || 0;
  error.messageKey = CREATE_ERROR_MESSAGE_KEYS[error.errorCode] || "errorMessage";
  return error;
};

const createClipRequestRequest = (variables) =>
  clientMutation(CREATE_CLIP_REQUEST, variables, endpoints.GRAPHQL_GENERAL)
    .then((data) => {
      if (!data?.createClipRequest) throw new Error("Clip has not been created");
      return data.createClipRequest;
    })
    .catch((exc) => {
      throw toCreateClipError(exc);
    });

const createSmartWitnessClipRequestRequest = (variables) =>
  clientMutation(CREATE_SMARTWITNESS_CLIP_REQUEST, variables, endpoints.GRAPHQL_GENERAL_V2).then((data) => {
    const response = data?.createSmartWitnessClipRequest;
    if (!response || response.error) {
      const error = new Error(response?.errorInfo?.Message || "Clip has not been created");
      error.errorInfo = response?.errorInfo;
      throw error;
    }
    return response.data;
  });

const updateClipRequestRequest = (variables) =>
  clientMutation(UPDATE_CLIP_REQUEST, variables, endpoints.GRAPHQL_GENERAL).then((data) => {
    if (!data?.ClipRequest) throw new Error("Error while updating clip request");
    return data.ClipRequest;
  });

const deleteClipRequestsRequest = (ids) =>
  clientMutation(DELETE_CLIP_REQUESTS, { ids }, endpoints.GRAPHQL_GENERAL_V2).then((data) => data?.data);

const restoreClipRequestsRequest = (ids) =>
  clientMutation(buildBatchMutation("Restore", "restoreClipRequest", ids), {}, endpoints.GRAPHQL_GENERAL).then((data) => {
    if (!data?.Restore0) throw new Error("Error while restoring clip requests");
    return data;
  });

export const createClipRequest = withInvalidation(createClipRequestRequest, () => [clipRequestKeys.all]);
export const createSmartWitnessClipRequest = withInvalidation(createSmartWitnessClipRequestRequest, () => [clipRequestKeys.all]);
export const updateClipRequest = withInvalidation(updateClipRequestRequest, (variables) => [clipRequestKeys.all, clipRequestKeys.detail(variables?.id)]);
export const deleteClipRequests = withInvalidation(deleteClipRequestsRequest, () => [clipRequestKeys.all]);
export const restoreClipRequests = withInvalidation(restoreClipRequestsRequest, () => [clipRequestKeys.all]);

// ---- Hooks ----
export const useClipRequestDetail = (id, options = {}) =>
  useQuery({
    queryKey: clipRequestKeys.detail(id),
    queryFn: () => getClipRequest(id),
    enabled: Boolean(id),
    ...options,
  });

export const useCreateClipRequest = (options) => useMutation({ mutationFn: createClipRequest, ...options });
export const useCreateSmartWitnessClipRequest = (options) => useMutation({ mutationFn: createSmartWitnessClipRequest, ...options });
export const useUpdateClipRequest = (options) => useMutation({ mutationFn: updateClipRequest, ...options });
export const useDeleteClipRequests = (options) => useMutation({ mutationFn: deleteClipRequests, ...options });
export const useRestoreClipRequests = (options) => useMutation({ mutationFn: restoreClipRequests, ...options });
